// File: frontend/src/pages/InvestorPitch/PitchDeck.js

import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import useSlideNavigation from './hooks/useSlideNavigation';
import SlideNavigation from './components/navigation/SlideNavigation';
import SlideQuickNav from './components/navigation/SlideQuickNav';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../config/constants';
import styles from './PitchDeck.module.css';

const PitchDeck = ({ slides, deckId: propDeckId, enterpriseHash, requireAuth = true }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(!requireAuth);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessData, setAccessData] = useState(null);
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  const [isEmailValid, setIsEmailValid] = useState(false);
  
  // Determine the correct deckId based on URL
  const determineDeckIdFromUrl = () => {
    const path = location.pathname;
    
    // For the new URL format
    if (path.includes('/investor/series-a/')) {
      return 'investor-series-a';
    } else if (path.includes('/investor/seed/')) {
      return 'investor-seed';
    } else if (path.includes('/customer/insurers/')) {
      return 'customer-insurers';
    } else if (path.includes('/customer/brokers/')) {
      return 'customer-brokers';
    }
    
    return propDeckId;
  };
  
  // Use the determined deckId
  const actualDeckId = determineDeckIdFromUrl();
  
  // Format deck name for display
  const formatDeckName = (deckId) => {
    if (!deckId) return '';
    
    // Split by hyphen and capitalize each word
    return deckId.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Get formatted deck name
  const deckName = formatDeckName(actualDeckId);

  // Fetch enterprise info on initial load
  useEffect(() => {
    const fetchEnterpriseInfo = async () => {
      try {
        // Only fetch if we have an enterprise hash
        if (enterpriseHash) {
          const response = await apiService.post(API_ENDPOINTS.ENTERPRISE.PITCH.VERIFY_ACCESS, {
            deckId: actualDeckId,
            fetchInfoOnly: true,
            enterpriseHash
          });
          
          if (response.success && response.enterpriseInfo) {
            setEnterpriseInfo(response.enterpriseInfo);
          }
        }
      } catch (err) {
        console.error('Error fetching enterprise info:', err);
        // Non-critical error, don't show to user
      }
    };
    
    fetchEnterpriseInfo();
  }, [actualDeckId, enterpriseHash]);

  // Format enterprise name with masking
  const formatEnterpriseName = (name) => {
    if (!name) return '';
    
    return name.split(' ')
      .map(word => {
        if (word.length <= 2) return word;
        return word.substring(0, 2) + '***';
      })
      .join(' ');
  };

  // Validate email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
  };

  const {
    currentSlide,
    direction,
    slideVariants,
    goToNextSlide,
    goToPreviousSlide,
    goToSlide,
    handleKeyboardNavigation
  } = useSlideNavigation(slides.length);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardNavigation);
    return () => window.removeEventListener('keydown', handleKeyboardNavigation);
  }, [handleKeyboardNavigation]);

  // Check if user has access from localStorage
  useEffect(() => {
    if (!requireAuth) return;
    
    const storedAccess = localStorage.getItem(`pitch_access_${actualDeckId}`);
    if (storedAccess) {
      try {
        const accessData = JSON.parse(storedAccess);
        const now = new Date();
        const expiryDate = new Date(accessData.expires_at);
        
        // Verify that the stored access matches the current enterprise hash
        const storedEnterpriseHash = accessData.enterprise_hash || 
          (accessData.enterprise_id ? accessData.enterprise_id.substring(0, 8) : null);
        
        if (now < expiryDate && (!enterpriseHash || storedEnterpriseHash === enterpriseHash)) {
          setIsAuthorized(true);
          setAccessData(accessData);
          
          // Update access count in the background
          updateAccessCount(accessData.access_id).catch(err => {
            console.error('Error updating access count:', err);
          });
        }
      } catch (err) {
        console.error('Error parsing stored access:', err);
      }
    }
  }, [actualDeckId, enterpriseHash, requireAuth]);

  const updateAccessCount = async (accessId) => {
    try {
      await apiService.put(API_ENDPOINTS.ENTERPRISE.PITCH.UPDATE_ACCESS_COUNT, {
        accessId
      });
    } catch (error) {
      console.error('Failed to update access count:', error);
      // Non-critical error, so we don't need to show it to the user
    }
  };

  const handleVerifyAccess = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.ENTERPRISE.PITCH.VERIFY_ACCESS, {
        email,
        deckId: actualDeckId
      });
      
      if (response.success) {
        // Extract enterprise hash from response
        const accessEnterpriseHash = response.access.enterprise_hash || 
          (response.access.enterprise_id ? response.access.enterprise_id.substring(0, 8) : null);
        
        // If we don't have an enterprise hash from the URL, don't do the comparison
        if (!enterpriseHash || accessEnterpriseHash === enterpriseHash) {
          setIsAuthorized(true);
          setAccessData(response.access);
          
          // Store access in localStorage with expiry
          localStorage.setItem(`pitch_access_${actualDeckId}`, JSON.stringify(response.access));
          
          // Update access count
          updateAccessCount(response.access.access_id);
        } else {
          setError('Access denied. The provided email does not have access to this specific pitch deck.');
        }
      } else {
        setError(response.message || 'Access denied. Please contact the administrator.');
      }
    } catch (err) {
      console.error('Error verifying access:', err);
      if (err.status === 403) {
        setError('Access denied. Please contact the administrator.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthorized) {
    // Get masked enterprise name if available
    const maskedEnterpriseName = enterpriseInfo?.name 
      ? formatEnterpriseName(enterpriseInfo.name)
      : null;
      
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <h2 className={styles.authTitle}>
            {t('investor_pitch.auth.title', { deckName: deckName })}
          </h2>
          
          <p className={styles.authDescription}>
            {t('investor_pitch.auth.new_description')}
            {maskedEnterpriseName && (
              <>
                {/* <br /><br /> */}
                {/* {t('investor_pitch.auth.prepared_for')} <span className={styles.enterpriseName}>{maskedEnterpriseName}</span> */}
              </>
            )}
          </p>
          
          <form onSubmit={handleVerifyAccess} className={styles.authForm}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                {t('investor_pitch.auth.email_label')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                className={styles.input}
                placeholder={t('investor_pitch.auth.email_placeholder')}
                required
              />
            </div>
            
            {error && <div className={styles.error}>{error}</div>}
            
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading || !isEmailValid}
            >
              {isLoading ? t('investor_pitch.auth.loading') : t('investor_pitch.auth.unlock_materials')}
            </button>

            <div className={styles.deckInfo}>
            {t('investor_pitch.auth.prepared_for')} <span className={styles.enterpriseName}>{maskedEnterpriseName}</span>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.slideQuickNav}>
            <SlideQuickNav
              currentSlide={currentSlide}
              totalSlides={slides.length}
              slides={slides}
              onSlideSelect={goToSlide}
            />
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className={styles.slideContainer}
          >
            {slides[currentSlide].content}
          </motion.div>
        </AnimatePresence>
      </main>

      <SlideNavigation
        currentSlide={currentSlide}
        totalSlides={slides.length}
        onNext={goToNextSlide}
        onPrevious={goToPreviousSlide}
      />

      <footer className={styles.footer}>
        <div className={styles.footerInfo}>
          <div className={styles.confidentialInfo}>
            <span className={styles.confidential}>
              {t('investor_pitch.master.common.confidential')}
            </span>
            <span className={styles.separator}>•</span>
            <span>
              {t('investor_pitch.master.common.prepared_for')} {accessData?.enterprise_name || process.env.REACT_APP_ENTERPRISE_NAME}
            </span>
            <span className={styles.separator}>•</span>
            <span className={styles.enterprise_id}>
              ID: {accessData?.enterprise_id || process.env.REACT_APP_ENTERPRISE_ID}
            </span>
          </div>
          <div className={styles.copyright}>
            © {new Date().getFullYear()} {t('investor_pitch.master.common.company')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PitchDeck;